import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import usePresentPage from 'client/hooks/usePresentPage'
import { isWebsitePageType } from 'client/store/page/pageSelectors'
import { pageTypes } from 'common/constants/pageTypes'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnnotationHeartIcon } from 'src/editor/shared/icons/annotation-heart-icon'
import { CurrencyDollarCircleIcon } from 'src/editor/shared/icons/currency-dollar-circle-icon'
import { FileCheck03Icon } from 'src/editor/shared/icons/file-check-03-icon'
import { FlexAlignBottomIcon } from 'src/editor/shared/icons/flex-align-bottom-icon'
import { FlexAlignTopIcon } from 'src/editor/shared/icons/flex-align-top-icon'
import { HomeSmileIcon } from 'src/editor/shared/icons/home-smile-icon'
import { Lightning01Icon } from 'src/editor/shared/icons/lightning-01-icon'
import { Save01Icon } from 'src/editor/shared/icons/save-01-icon'
import { ShoppingCart01Icon } from 'src/editor/shared/icons/shopping-cart-01-icon'
import { ThumbsUpIcon } from 'src/editor/shared/icons/thumbs-up-icon'
import { Users03Icon } from 'src/editor/shared/icons/users-03-icon'
import { blockCategories, blockCategoryNames } from '../consts'
import { BlockCategoryType, BlockType } from '../types'
import { BlockCategory } from './block-category'

const blockCategoryIcons: Partial<
  Record<BlockCategoryType, React.JSX.Element>
> = {
  [blockCategories.optInForms]: (
    <FileCheck03Icon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.features]: (
    <ThumbsUpIcon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.teamMembers]: (
    <Users03Icon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.orderForms]: (
    <ShoppingCart01Icon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.hero]: (
    <HomeSmileIcon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.pricePlans]: (
    <CurrencyDollarCircleIcon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.pageHeaders]: (
    <FlexAlignTopIcon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.pageFooters]: (
    <FlexAlignBottomIcon className="h-5 w-5 fill-iconSecondary" />
  ),
  [blockCategories.testimonials]: (
    <AnnotationHeartIcon className="h-5 w-5 fill-iconSecondary" />
  ),
}

interface BlockCategoriesProps {
  pageType: PageTypeEnum
  setActiveCategory: (category: BlockCategoryType) => void
}

export const BlockCategories = ({
  pageType,
  setActiveCategory,
}: BlockCategoriesProps) => {
  const { t } = useTranslation()
  const commonBlocksByCategories: Record<BlockCategoryType, BlockType[]> =
    useBlocks(blocksSelectors.getCommonBlocksByCategories)

  const isOfferPageOrNotOrderFormCategory = (category: string) =>
    pageType === pageTypes.offerPage || category !== blockCategories.orderForms

  const { isTemplate } = usePageRouteDetector()
  const userBlocks: BlockType[] = useBlocks(blocksSelectors.getUserBlocks)
  const masterBlocks: BlockType[] = useBlocks(blocksSelectors.getMasterBlocks)
  const isWebsitePage = usePresentPage(isWebsitePageType)

  // no common blocks allowed for website pages except for those created by user himself
  const allowedCommonCategories = Object.keys(commonBlocksByCategories)
    .filter(isOfferPageOrNotOrderFormCategory)
    .map(category => ({
      name: t(blockCategoryNames[category as BlockCategoryType]),
      type: category as BlockCategoryType,
      icon: blockCategoryIcons[category as BlockCategoryType],
    }))

  const isMasterBlockAvailable = useMemo(() => {
    return !isTemplate && masterBlocks.length > 0 && !isWebsitePage
  }, [isTemplate, masterBlocks.length, isWebsitePage])
  const isUserBlockAvailable = useMemo(() => {
    return userBlocks.length > 0
  }, [userBlocks.length])
  const allowedBlockAvailable = useMemo(() => {
    return allowedCommonCategories.length > 0
  }, [allowedCommonCategories.length])

  const hasAnyBlocks: boolean =
    isMasterBlockAvailable || isUserBlockAvailable || allowedBlockAvailable

  return (
    <div className="flex flex-col gap-1">
      {!hasAnyBlocks && (
        <div className="mt-12 text-center text-textTertiary">
          <span className="text-S">{t('components.core.blocks.empty')}</span>
        </div>
      )}

      {isMasterBlockAvailable && (
        <BlockCategory
          label={t(blockCategoryNames.userMaster)}
          onClick={() => setActiveCategory(blockCategories.userMaster)}
          icon={<Lightning01Icon className="h-5 w-5 fill-iconSecondary" />}
        />
      )}
      {isUserBlockAvailable && (
        <BlockCategory
          label={t(blockCategoryNames.userSimple)}
          onClick={() => setActiveCategory(blockCategories.userSimple)}
          icon={<Save01Icon className="h-5 w-5 fill-iconSecondary" />}
        />
      )}
      {allowedBlockAvailable &&
        allowedCommonCategories.map(category => (
          <BlockCategory
            key={category.type}
            label={category.name}
            onClick={() => setActiveCategory(category.type)}
            icon={category.icon}
          />
        ))}
    </div>
  )
}
